import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead } from "@/components/shared";

const NIGHTLY_BASE = { 1: 220, 2: 320, 3: 450, 4: 600, 5: 800, 6: 1000 };
const PREMIUM_PARISHES = ["Southampton", "Warwick", "Paget", "St. George's"];
const PARISHES = ["St. George's", "Hamilton Parish", "Smith's", "Devonshire", "Pembroke", "Paget", "Warwick", "Southampton", "Sandys"];

export default function Estimator() {
  const [bedrooms, setBedrooms] = useState(3);
  const [parish, setParish] = useState("Warwick");
  const [occupancy, setOccupancy] = useState(70);

  const { nightly, monthly } = useMemo(() => {
    const tier = PREMIUM_PARISHES.includes(parish) ? 1.12 : 1.0;
    const n = Math.round(NIGHTLY_BASE[bedrooms] * tier);
    return { nightly: n, monthly: Math.round(n * 30.4 * (occupancy / 100) / 10) * 10 };
  }, [bedrooms, parish, occupancy]);

  return (
    <section className="bg-white" data-testid="earnings-estimator">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHead
              overline="Earnings Estimator"
              title={<>What could your property<br /><span className="italic text-bermuda-dark">earn each month?</span></>}
              copy="Slide, select and see an instant illustration of your Bermuda property's potential. Your property assessment includes real, specific projections for your home."
            />
            <Reveal delay={0.2}>
              <div className="mt-10 space-y-9">
                <div>
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="est-bedrooms" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/70">Bedrooms</label>
                    <span className="font-mono text-sm text-navy" data-testid="est-bedrooms-value">{bedrooms}{bedrooms === 6 ? "+" : ""}</span>
                  </div>
                  <input
                    id="est-bedrooms"
                    data-testid="est-bedrooms-slider"
                    type="range" min="1" max="6" step="1" value={bedrooms}
                    onChange={(e) => setBedrooms(Number(e.target.value))}
                    className="mt-3 w-full accent-[#C48B7F]"
                  />
                </div>
                <div>
                  <label htmlFor="est-parish" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/70">Parish</label>
                  <select
                    id="est-parish"
                    data-testid="est-parish-select"
                    value={parish}
                    onChange={(e) => setParish(e.target.value)}
                    className="mt-3 w-full appearance-none border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none focus:border-bermuda-dark"
                  >
                    {PARISHES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="est-occupancy" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/70">Occupancy</label>
                    <span className="font-mono text-sm text-navy" data-testid="est-occupancy-value">{occupancy}%</span>
                  </div>
                  <input
                    id="est-occupancy"
                    data-testid="est-occupancy-slider"
                    type="range" min="40" max="95" step="5" value={occupancy}
                    onChange={(e) => setOccupancy(Number(e.target.value))}
                    className="mt-3 w-full accent-[#C48B7F]"
                  />
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="flex">
            <div className="noise-overlay relative flex w-full flex-col justify-center bg-navy p-10 text-ivory lg:p-14">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Illustrative Estimate</p>
              <div className="mt-8 flex items-end justify-between border-b border-white/10 pb-6">
                <span className="text-sm text-ivory/60">Suggested nightly rate</span>
                <span className="font-mono text-3xl" data-testid="est-nightly-rate">${nightly}</span>
              </div>
              <div className="mt-8">
                <span className="text-sm text-ivory/60">Estimated monthly gross</span>
                <p className="mt-2 font-serif text-6xl font-light tracking-tight sm:text-7xl" data-testid="est-monthly-revenue">
                  ${monthly.toLocaleString()}
                </p>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-ivory/50">
                Based on typical Bermuda vacation-rental rates for comparable homes. Cleaning fees, seasonality,
                events and your home's specific appeal all move the real number — usually up.
              </p>
              <Link
                to="/assessment"
                data-testid="estimator-assessment-btn"
                className="group mt-9 inline-flex w-fit items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory"
              >
                Get My Exact Assessment
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/for-homeowners#calculator" data-testid="estimator-calculator-link" className="mt-4 block text-xs text-ivory/60 underline underline-offset-4 transition-colors hover:text-bermuda">
                Want the full profit breakdown? Try the Owner's Calculator →
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
